import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  getPaymentAuthenticationRequired,
  isApiError,
  isTerminalPaymentAttemptError,
  updatePaymentAuthenticationForError,
} from "@bw/shared/api";
import type { CoachingPayment, CoachingFundingInput } from "@bw/shared/api";
import type { SavedPaymentMethodResponse } from "@bw/shared/types/generated";
import {
  formatMoney,
  reconcileSavedPaymentMethodSelection,
} from "@bw/shared/utils";
import { paymentQueryKeys, useFundCoachingProposal } from "@bw/shared/hooks";
import {
  useLessonPaymentMethods,
  useLessonFrontendConfig,
  useLessonSetupIntent,
} from "../hooks/usePayment";
import { coachingPaymentPrice } from "./paymentPrice";
import { PaymentMethodsLoadError } from "../../../common/components/PaymentMethodsLoadError";
import {
  MaterialAddCardForm,
  MaterialConnectedElementsProvider,
  MaterialPaymentIntentAuthentication,
  MaterialPaymentMethodPicker,
} from "../../../common/components/payment";

export function CoachingPaymentPanel({
  payment,
  canAct,
}: {
  payment: CoachingPayment;
  canAct: () => boolean;
}) {
  const displayPrice = coachingPaymentPrice(payment);
  const disclosedPrice = displayPrice !== null;
  const facilityId = payment.facility_id;
  const price = displayPrice ?? "—";

  const methodsQ = useLessonPaymentMethods(facilityId, true);
  const configQ = useLessonFrontendConfig(facilityId, true);
  const setupIntent = useLessonSetupIntent();
  const payLesson = useFundCoachingProposal();
  const attemptRef = useRef<[string, CoachingFundingInput] | null>(null);
  const payBusy = useRef(false);
  const [skipPromotion, setSkipPromotion] = useState(false);
  const [attemptLocked, setAttemptLocked] = useState(false);
  const [lockedReview, setLockedReview] = useState<{
    campaign: CoachingPayment["campaign"];
    currency: string;
    payLabel: string;
    paymentMethod: SavedPaymentMethodResponse;
    paymentMethodId: string;
    skipPromotion: boolean;
    subtotalAmount: CoachingPayment["subtotal_amount"];
  } | null>(null);

  const paymentSelectionContext = `${facilityId}:${payment.acceptance_id}`;
  const [paymentSelection, setPaymentSelection] = useState(() => ({
    context: paymentSelectionContext,
    paymentMethodId: null as string | null,
  }));
  const selectedPmId =
    paymentSelection.context === paymentSelectionContext
      ? paymentSelection.paymentMethodId
      : null;
  const selectPaymentMethod = useCallback(
    (paymentMethodId: string | null) => {
      setPaymentSelection({
        context: paymentSelectionContext,
        paymentMethodId,
      });
    },
    [paymentSelectionContext],
  );
  const initializedPaymentContextRef = useRef<string | null>(null);
  const [cardSetupBusy, setCardSetupBusy] = useState(false);
  const [paymentAuthentication, setPaymentAuthentication] = useState<{
    clientSecret: string;
    body: [string, CoachingFundingInput];
  } | null>(null);

  const createSetupIntentSecret = useCallback(
    async (idempotencyKey: string) => {
      const res = await setupIntent.mutateAsync(idempotencyKey);
      return res.client_secret;
    },
    [setupIntent],
  );

  const methods = methodsQ.data ?? [];
  const paymentMethodsSettled =
    methodsQ.data !== undefined && !methodsQ.isFetching && !methodsQ.isError;
  useEffect(() => {
    if (!paymentMethodsSettled || methodsQ.data === undefined) return;
    const initializeProviderDefault =
      initializedPaymentContextRef.current !== paymentSelectionContext;
    initializedPaymentContextRef.current = paymentSelectionContext;
    setPaymentSelection((current) => {
      const currentId =
        current.context === paymentSelectionContext
          ? current.paymentMethodId
          : null;
      const paymentMethodId = reconcileSavedPaymentMethodSelection(
        methodsQ.data,
        currentId,
        initializeProviderDefault,
      );
      if (
        current.context === paymentSelectionContext &&
        current.paymentMethodId === paymentMethodId
      ) {
        return current;
      }
      return { context: paymentSelectionContext, paymentMethodId };
    });
  }, [methodsQ.data, paymentMethodsSettled, paymentSelectionContext]);
  const chargeReadyPmId =
    paymentMethodsSettled && methodsQ.data !== undefined
      ? reconcileSavedPaymentMethodSelection(methodsQ.data, selectedPmId, false)
      : null;

  function handlePay() {
    const lockedBody = attemptLocked ? attemptRef.current : null;
    const paymentMethodId =
      lockedBody?.[1].payment_method_id ?? chargeReadyPmId;
    const paymentMethod =
      lockedReview?.paymentMethod ??
      methods.find((method) => method.payment_method_id === paymentMethodId);
    if (
      !canAct() ||
      (lockedReview === null && (!payment.can_fund || !disclosedPrice)) ||
      !paymentMethodId ||
      !paymentMethod ||
      payLesson.isPending ||
      payBusy.current ||
      cardSetupBusy ||
      (attemptLocked && lockedBody === null)
    )
      return;
    payBusy.current = true;
    setAttemptLocked(true);
    const campaignId = lockedBody
      ? lockedBody[1].campaign_id
      : skipPromotion
        ? null
        : (payment.campaign?.campaign_id ?? null);
    const body: [string, CoachingFundingInput] =
      lockedBody ??
      (attemptRef.current?.[1].payment_method_id === paymentMethodId &&
      attemptRef.current?.[1].campaign_id === campaignId &&
      attemptRef.current?.[1].skip_promotions === skipPromotion
        ? attemptRef.current
        : [
            payment.acceptance_id,
            {
              revision_id: payment.revision_id,
              payment_method_id: paymentMethodId,
              idempotency_key: crypto.randomUUID(),
              campaign_id: campaignId,
              skip_promotions: skipPromotion,
            },
          ]);
    attemptRef.current = body;
    if (lockedReview === null) {
      setLockedReview({
        campaign: payment.campaign,
        currency: payment.currency,
        payLabel:
          skipPromotion && payment.subtotal_amount != null
            ? formatMoney(payment.subtotal_amount, payment.currency)
            : price,
        paymentMethod,
        paymentMethodId,
        skipPromotion,
        subtotalAmount: payment.subtotal_amount,
      });
    }
    payLesson.mutate(body, {
      onError: (error) => {
        const authentication = getPaymentAuthenticationRequired(error);
        if (authentication) {
          setPaymentAuthentication({
            clientSecret: authentication.clientSecret,
            body,
          });
        } else if (isTerminalPaymentAttemptError(error)) {
          attemptRef.current = null;
          setAttemptLocked(false);
          setLockedReview(null);
        }
      },
      onSuccess: () => {
        attemptRef.current = null;
        setAttemptLocked(false);
        setLockedReview(null);
      },
      onSettled: () => {
        payBusy.current = false;
      },
    });
  }

  async function finishPaymentAuthentication(
    authenticatedClientSecret: string,
  ) {
    const authentication = paymentAuthentication;
    if (
      !authentication ||
      authentication.clientSecret !== authenticatedClientSecret
    ) {
      return;
    }
    try {
      await payLesson.mutateAsync(authentication.body);
      attemptRef.current = null;
      setAttemptLocked(false);
      setLockedReview(null);
      setPaymentAuthentication((current) =>
        current?.clientSecret === authenticatedClientSecret ? null : current,
      );
    } catch (error) {
      if (isTerminalPaymentAttemptError(error)) {
        attemptRef.current = null;
        setAttemptLocked(false);
        setLockedReview(null);
      }
      setPaymentAuthentication((current) =>
        updatePaymentAuthenticationForError(
          current,
          authenticatedClientSecret,
          error,
        ),
      );
      throw error;
    }
  }

  const err = payLesson.error;
  const reviewedCampaign = lockedReview?.campaign ?? payment.campaign;
  const reviewedSkipPromotion = lockedReview?.skipPromotion ?? skipPromotion;
  const reviewedCurrency = lockedReview?.currency ?? payment.currency;
  const reviewedSubtotal =
    lockedReview?.subtotalAmount ?? payment.subtotal_amount;
  const reviewedMethods = lockedReview ? [lockedReview.paymentMethod] : methods;
  const errMessage = paymentAuthentication
    ? null
    : isApiError(err)
      ? err.message
      : err
        ? "Payment failed. Please try again."
        : null;

  return (
    <Stack
      component="section"
      spacing={1.5}
      aria-label="Payment"
      sx={{ mt: 2 }}
    >
      {reviewedCampaign ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={0.75}>
            <Typography variant="overline">
              {reviewedSkipPromotion ? "Offer not applied" : "Coaching offer"}
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>
              {reviewedCampaign.name}
            </Typography>
            {reviewedSkipPromotion ? (
              <Typography variant="body2" color="text.secondary">
                You chose not to use this offer for this payment.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Original price{" "}
                {formatMoney(reviewedCampaign.subtotal, reviewedCurrency)} ·
                Savings{" "}
                {formatMoney(reviewedCampaign.discount, reviewedCurrency)}
              </Typography>
            )}
            <Typography variant="h6">
              {reviewedSkipPromotion ? "Regular price" : "Total"}{" "}
              {formatMoney(
                reviewedSkipPromotion && reviewedSubtotal != null
                  ? reviewedSubtotal
                  : reviewedCampaign.total,
                reviewedCurrency,
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Final eligibility is confirmed again when you pay. This offer does
              not change the accepted lesson schedule or cancellation terms.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reviewedSkipPromotion}
                  onChange={(event) => {
                    setSkipPromotion(event.target.checked);
                    attemptRef.current = null;
                  }}
                  disabled={attemptLocked}
                />
              }
              label="Continue without this offer"
            />
          </Stack>
        </Paper>
      ) : null}
      {lockedReview === null && configQ.isError ? (
        <Alert severity="error">
          This facility can&apos;t take card payments yet. Please try again
          later.
        </Alert>
      ) : lockedReview === null &&
        methodsQ.isError &&
        paymentAuthentication === null ? (
        <PaymentMethodsLoadError
          retrying={methodsQ.isFetching}
          onRetry={() => void methodsQ.refetch()}
        />
      ) : lockedReview === null && (configQ.isLoading || methodsQ.isLoading) ? (
        <Stack
          direction="row"
          spacing={1.5}
          role="status"
          sx={{ alignItems: "center" }}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">
            Loading payment options…
          </Typography>
        </Stack>
      ) : (
        <>
          <MaterialPaymentMethodPicker
            methods={reviewedMethods}
            selectedId={lockedReview?.paymentMethodId ?? selectedPmId}
            onSelect={selectPaymentMethod}
            disabled={
              payLesson.isPending ||
              attemptLocked ||
              paymentAuthentication !== null ||
              cardSetupBusy
            }
            allowRemove
            facilityId={facilityId}
            paymentMethodsQueryKey={paymentQueryKeys.paymentMethods(facilityId)}
          />
          {configQ.data ? (
            <MaterialConnectedElementsProvider config={configQ.data}>
              {paymentAuthentication ? (
                <MaterialPaymentIntentAuthentication
                  clientSecret={paymentAuthentication.clientSecret}
                  onAuthenticated={finishPaymentAuthentication}
                />
              ) : attemptLocked ? (
                <Typography variant="body2" color="text.secondary">
                  The offer and payment method are locked for this payment
                  attempt. Retry to continue with the same reviewed terms.
                </Typography>
              ) : (
                <MaterialAddCardForm
                  publishableKey={configQ.data.publishable_key}
                  createSetupIntent={createSetupIntentSecret}
                  onBusyChange={setCardSetupBusy}
                  onCardAdded={async (pmId) => {
                    const refreshed = await methodsQ.refetch();
                    const savedMethod = refreshed.data?.find(
                      (method) => method.payment_method_id === pmId,
                    );
                    if (refreshed.isError || savedMethod?.is_usable !== true) {
                      throw new Error(
                        "The saved card is not available yet. Retry to refresh it.",
                      );
                    }
                    selectPaymentMethod(pmId);
                  }}
                />
              )}
            </MaterialConnectedElementsProvider>
          ) : null}
        </>
      )}

      {errMessage ? <Alert severity="error">{errMessage}</Alert> : null}

      {paymentAuthentication ? null : (
        <Button
          variant="contained"
          size="small"
          onClick={handlePay}
          disabled={
            (lockedReview === null && (!payment.can_fund || !disclosedPrice)) ||
            payLesson.isPending ||
            cardSetupBusy ||
            !(lockedReview?.paymentMethodId ?? chargeReadyPmId)
          }
          sx={{ "&.Mui-disabled": { pointerEvents: "auto" } }}
        >
          {payLesson.isPending
            ? "Paying…"
            : `Pay ${lockedReview?.payLabel ?? (skipPromotion && payment.subtotal_amount != null ? formatMoney(payment.subtotal_amount, payment.currency) : price)}`}
        </Button>
      )}
    </Stack>
  );
}
